import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import useCustomFecthQuery from "../../../../Hooks/useCustomFecthQuery";
// Removed useCustomMutation import since we are using fetch directly
import ViewTeam from "./ViewTeam";
import EditTeam from "./EditTeam";
import ModalDelete from "../../../Modal/ModalDelete";
import DeleteForm from "./DeleteForm";
import OffCanvas from "../../../OffCanvas/OffCanvas";
import { FaEdit } from "react-icons/fa";
import { AiFillEye, AiFillDelete } from "react-icons/ai";
import { useQueryClient } from "@tanstack/react-query"; // ✅ Import for cache invalidation
import { toast } from "react-toastify"; // ✅ Import for notifications

const TableOrganizer = ({ userID, token, eventID, search, onEdit }) => {
  const queryClient = useQueryClient(); // ✅ Initialize query client
  
  const [speakerEmail, setSpeakerEmail] = useState(null);
  const [speakerDetails, setSpeakerDetails] = useState(null);
  const [isToggleView, setIsToggleView] = useState(false);
  const [isToggleDelete, setIsToggleDelete] = useState(false);
  const [isEditOffcanvasOpen, setIsEditOffcanvasOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false); // ✅ Local loading state
  
  const key = ["organizerTeam", eventID];

  // Removed mutationDelete since we use fetch now

  const {
    data: getEventDetails,
    error: errorGetEventDetails,
    isLoading: isLoadingEventDetails,
  } = useCustomFecthQuery(
    key,
    `${process.env.REACT_APP_API}events/${eventID}`,
    token,
    {
      enabled: !!eventID,
    }
  );

  const tog_editTeam = (details) => {
    setSpeakerDetails(details);
    setIsToggleView(!isToggleView);
  };
  
  const tog_deleteTeam = (id, email) => {
    setSpeakerEmail(email);
    setIsToggleDelete(!isToggleDelete);
  };
  
  const handleEditClick = (member) => {
    if (onEdit) {
      onEdit(member);
    } else {
      tog_editTeam(member);
    }
  };

  // ✅ REPLACED useCustomMutation with direct fetch
  const DeleteTeam = async () => {
    if (!speakerEmail) {
      toast.error("Email is missing. Cannot delete.");
      return;
    }

    setIsDeleting(true);
    const url = `${process.env.REACT_APP_API}events/remove-team-member/${eventID}`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json', // ✅ Explicitly set header
        },
        body: JSON.stringify({ email: speakerEmail }),
      });

      if (response.ok) {
        toast.success("Team member deleted successfully!");
        
        // ✅ Invalidate query to refresh the table immediately
        await queryClient.invalidateQueries({ queryKey: key });
        
        setIsToggleDelete(false);
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || "Failed to delete team member.");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Network error. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const getRoleDisplay = (role) => {
    if (!role) return "N/A";
    const roleLower = role.toLowerCase();
    if (roleLower === "event_organizer" || roleLower === "admin") return "Admin";
    if (roleLower === "securityguard" || roleLower === "security") return "Security";
    if (roleLower === "registrationofficer" || roleLower.includes("support")) return "Support Desk";
    if (roleLower === "co_event_organizer" || roleLower === "co organizer") return "Co Organizer";
    if (roleLower === "event_org_marketing" || roleLower === "marketing") return "Marketing";
    if (roleLower === "event_org_sales" || roleLower === "sales") return "Sales";
    if (roleLower === "host" || roleLower === "event host") return "Event Host";
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  const columns = [
    {
      name: <span className="font-weight-bold fs-13">First name</span>,
      cell: (row) => <>{row?.firstName || "N/A"}</>,
    },
    {
      name: <span className="font-weight-bold fs-13">Last name</span>,
      cell: (row) => <>{row?.lastName || "N/A"}</>,
    },
    {
      name: <span className="font-weight-bold fs-13">Phone number</span>,
      cell: (row) => <>{row?.phoneNumber || "N/A"}</>,
    },
    {
      name: <span className="font-weight-bold fs-13">Email</span>,
      selector: (row) => row?.email || "N/A",
    },
    {
      name: <span className="font-weight-bold fs-13">Role</span>,
      cell: (row) => {
        const roleValue = row?.role || row?.roles;
        return <>{getRoleDisplay(roleValue)}</>;
      },
    },
    {
      name: <span className="font-weight-bold fs-13">Action</span>,
      cell: (row) => {
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <AiFillEye
              size={20}
              style={{ cursor: "pointer", color: "#64748b" }}
              onClick={() => tog_editTeam(row)}
              title="View Details"
            />
            <FaEdit
              size={18}
              style={{ cursor: "pointer", color: "#f68a20" }}
              onClick={() => handleEditClick(row)}
              title="Edit Member"
            />
            <AiFillDelete
              size={20}
              style={{ cursor: "pointer", color: "#ef4444" }}
              onClick={() => tog_deleteTeam(row?._id, row?.email)}
              title="Delete Member"
            />
          </div>
        );
      },
    },
  ];

  const filterTeams = (teams, searchQuery) => {
    if (!searchQuery) return teams || [];
    return teams?.filter((item) =>
      [item?.firstName, item?.lastName, item?.email, item?.role, item?.roles].some((field) =>
        field?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  };
  
  const filteredEventTeam = filterTeams(getEventDetails?.data?.personnel, search);
  const data = filteredEventTeam || [];
  
  // Removed useEffect for mutationDelete.isSuccess since we handle it in DeleteTeam

  return (
    <div className="mt-3 w-100">
      <div className="analystic-table-card">
        <div className="w-100">
          <DataTable
            fixedHeader
            fixedHeaderScrollHeight="300px"
            columns={columns}
            responsive
            data={data}
            pagination
            noDataComponent={<div className="p-4 text-center text-muted">No team members found</div>}
          />
        </div>
      </div>
      
      <ModalDelete
        size="md"
        isOpen={isToggleDelete}
        toggle={() => setIsToggleDelete(false)}
        title="Delete Organizer Team"
        body={
          <DeleteForm
            setIsToggleDelete={setIsToggleDelete}
            DeleteParticitant={DeleteTeam}
            pending={isDeleting} // ✅ Use local loading state
          />
        }
      />
      
      <OffCanvas
        isOffcanvasOpen={isToggleView}
        title="Organizer Team Details"
        body={<ViewTeam details={speakerDetails} />}
        toggleSaveChanges={() => tog_editTeam()}
        directionOffCanvas="end"
        widthOffCanvas={400}
      />
    </div>
  );
};

export default TableOrganizer;